import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Button, message, Typography, Space } from 'antd';
import { MessageOutlined, UserOutlined, GlobalOutlined } from '@ant-design/icons';
import {
  getWidgetConversations,
  WidgetConversation,
  WidgetConversationsResponse,
} from '../../lib/widgetsApi';

const { Text } = Typography;

interface WidgetConversationsModalProps {
  visible: boolean;
  widgetId: string;
  onClose: () => void;
}

const WidgetConversationsModal: React.FC<WidgetConversationsModalProps> = ({
  visible,
  widgetId,
  onClose,
}) => {
  const [conversations, setConversations] = useState<WidgetConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ skip: 0, take: 20, total: 0 });

  useEffect(() => {
    if (visible && widgetId) {
      loadConversations();
    }
  }, [visible, widgetId, pagination.skip]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const data: WidgetConversationsResponse = await getWidgetConversations(
        widgetId,
        pagination.skip,
        pagination.take,
      );
      setConversations(data.conversations);
      setPagination((prev) => ({ ...prev, total: data.total }));
    } catch (error: any) {
      message.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: 200,
      render: (sessionId: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {sessionId.substring(0, 16)}...
        </code>
      ),
    },
    {
      title: 'Messages',
      dataIndex: 'messageCount',
      key: 'messageCount',
      width: 100,
      align: 'center' as const,
      render: (count: number) => (
        <Space>
          <MessageOutlined />
          {count}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Started',
      dataIndex: 'firstMessageAt',
      key: 'firstMessageAt',
      width: 150,
      render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastMessageAt',
      key: 'lastMessageAt',
      width: 150,
      render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
  ];

  const expandedRowRender = (record: WidgetConversation) => {
    return (
      <div className="p-4 bg-gray-50">
        {/* User Info */}
        {record.userInfo && Object.keys(record.userInfo).length > 0 && (
          <div className="mb-4">
            <Text strong>
              <UserOutlined /> User Information:
            </Text>
            <div className="mt-2">
              {Object.entries(record.userInfo).map(([key, value]) => (
                <div key={key} className="text-sm">
                  <Text type="secondary">{key}:</Text> {String(value)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Session Details */}
        <div className="mb-4">
          <Text strong>
            <GlobalOutlined /> Session Details:
          </Text>
          <div className="mt-2 text-sm">
            {record.referrer && (
              <div>
                <Text type="secondary">Referrer:</Text> {record.referrer}
              </div>
            )}
            {record.userAgent && (
              <div>
                <Text type="secondary">User Agent:</Text>{' '}
                {record.userAgent.substring(0, 100)}...
              </div>
            )}
            {record.ipAddress && (
              <div>
                <Text type="secondary">IP Address:</Text> {record.ipAddress}
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div>
          <Text strong>
            <MessageOutlined /> Conversation ({record.messageCount} messages):
          </Text>
          <div className="mt-2 max-h-96 overflow-y-auto">
            {record.messages && record.messages.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {record.messages.map((msg: any, idx: number) => (
                  <div
                    key={idx}
                    className={`p-3 rounded ${
                      msg.role === 'user' ? 'bg-blue-50 ml-8' : 'bg-white mr-8'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <Text strong className="text-xs">
                        {msg.role === 'user' ? 'User' : 'Assistant'}
                      </Text>
                      <Text type="secondary" className="text-xs">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </Text>
                    </div>
                    <Text className="text-sm">{msg.content}</Text>
                  </div>
                ))}
              </Space>
            ) : (
              <Text type="secondary">No messages</Text>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title="Widget Conversations"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
    >
      <Table
        columns={columns}
        dataSource={conversations}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowRender,
          expandIcon: ({ expanded, onExpand, record }) => (
            <Button
              size="small"
              onClick={(e) => onExpand(record, e)}
              icon={<MessageOutlined />}
            >
              {expanded ? 'Hide' : 'View'}
            </Button>
          ),
        }}
        pagination={{
          current: Math.floor(pagination.skip / pagination.take) + 1,
          pageSize: pagination.take,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} conversations`,
          onChange: (page, pageSize) => {
            setPagination({
              skip: (page - 1) * pageSize,
              take: pageSize,
              total: pagination.total,
            });
          },
        }}
      />
    </Modal>
  );
};

export default WidgetConversationsModal;
